import React, { useState, useEffect } from "react";
import {
  FaProjectDiagram,
  FaUsers,
  FaAward,
  FaChartLine,
  FaSearch,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ClearFilter from "../../components/ClearFilter";
import Pagination from "../../components/Pagination";
import "./ManageLearningPaths.css";
import { apiFetch } from '../../utils/api';

const ManageLearningPaths = () => {
  const [learningPaths, setLearningPaths] = useState([]);
  const [filteredPaths, setFilteredPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showDeleteLoading, setShowDeleteLoading] = useState(false);
  const navigate = useNavigate();

  const [stats, setStats] = useState([
    {
      label: "Active Paths",
      value: 0,
      icon: <FaProjectDiagram />,
      color: "blue",
    },
    {
      label: "Enrolled Members",
      value: 0,
      icon: <FaUsers />,
      color: "green",
    },
    { label: "Total Credits", value: 0, icon: <FaAward />, color: "purple" },
    {
      label: "Completion Rate",
      value: "0%",
      icon: <FaChartLine />,
      color: "orange",
    },
  ]);

  const headers = [
    "Learning Path Name",
    "Created By",
    "Total Credits",
    "Total Courses",
    "Start Date",
    "End Date",
    "Cost Estimation (INR)",
    "Action",
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const email = localStorage.getItem("userEmail");
      const [learningPathsData, statsData] = await Promise.all([
        apiFetch(`${window._env_.API_BASE_URL}/learning_paths/learning-paths?user_email=${email}`),
        apiFetch(`${window._env_.API_BASE_URL}/learning_paths/stats?user_email=${email}`),
      ]);

      const paths = Array.isArray(learningPathsData) ? learningPathsData : [];
      setLearningPaths(paths);
      setFilteredPaths(paths);

      setStats([
        {
          label: "Active Paths",
          value: statsData.active_paths || 0,
          icon: <FaProjectDiagram />,
          color: "blue",
        },
        {
          label: "Enrolled Members",
          value: statsData.enrolled_members || 0,
          icon: <FaUsers />,
          color: "green",
        },
        {
          label: "Total Credits",
          value: statsData.total_credits || 0,
          icon: <FaAward />,
          color: "purple",
        },
        {
          label: "Completion Rate",
          value: statsData.completion_rate || "0%",
          icon: <FaChartLine />,
          color: "orange",
        },
      ]);
    } catch (err) {
      console.error("Error fetching data:", err);
      setLearningPaths([]);
      setFilteredPaths([]);
      setSnackbar({
        open: true,
        message: "Error fetching data",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const search = e.target.value.toLowerCase();
    setFilters({ ...filters, search });
    const filtered = learningPaths.filter((lp) =>
      lp.name.toLowerCase().includes(search)
    );
    setFilteredPaths(filtered);
  };

  const handleClearFilters = () => {
    setFilters({ search: "" });
    setFilteredPaths(learningPaths);
  };

  const slugify = (text) => {
    return text
      .toLowerCase()
      .replace(/ /g, "-")
      .replace(/[^a-z0-9-]/g, "");
  };

  const handleEdit = (id) => {
    const learningPath = learningPaths.find((lp) => lp.id === id);
    if (learningPath) {
      const slug = slugify(learningPath.name);
      navigate(`/manage-learning-paths/${slug}-${learningPath.id}/overview`);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    setShowConfirmModal(false);
    setShowDeleteLoading(true);
    try {
      const email = localStorage.getItem("userEmail");
      const res = await apiFetch(
        `${window._env_.API_BASE_URL}/learning_paths/learning-path/${selectedId}?user_email=${encodeURIComponent(email)}`,
        { method: "DELETE" }
      );
      setSnackbar({
        open: true,
        message: res.message || "Learning Path deleted successfully!",
        type: "success",
      });
      fetchData();
    } catch (err) {
      console.error("Error deleting learning path:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to delete learning path",
        type: "error",
      });
    } finally {
      setShowDeleteLoading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const email = localStorage.getItem("userEmail");
      const res = await apiFetch(
        `${window._env_.API_BASE_URL}/learning_paths/create-learning-paths`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name,
            description: formData.description,
            created_by: email,
          }),
        }
      );

      setSnackbar({
        open: true,
        message: res.message || "Learning Path created successfully!",
        type: "success",
      });
      setShowForm(false);
      setFormData({ name: "", description: "" });
      fetchData();
    } catch (err) {
      console.error("Error creating learning path:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to create learning path",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => {
        setSnackbar({ ...snackbar, open: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPaths.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="mlp-container">
      <h2 className="mlp-title">Manage Learning Paths</h2>
      <p className="mlp-subtitle">
        Oversee and manage all learning paths created by your Department
      </p>

      <div className="mlp-stats">
        {stats.map((stat, i) => (
          <div key={i} className={`mlp-card ${stat.color}`}>
            <div className="mlp-icon">{stat.icon}</div>
            <div className="mlp-value">{stat.value}</div>
            <div className="mlp-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="mlp-toolbar">
        <div className="mlp-toolbar-left">
          <div className="mlp-search-wrapper">
            <FaSearch className="mlp-search-icon" />
            <input
              type="text"
              value={filters.search}
              onChange={handleSearchChange}
              placeholder="Search Learning Path Name..."
              className="mlp-search"
            />
          </div>
        </div>
        <div className="mlp-toolbar-right">
          <ClearFilter onClear={handleClearFilters} />
          <button
            className="mlp-create-btn"
            onClick={() => setShowForm(true)}
          >
            + Create New Learning Path
          </button>
        </div>
      </div>

      <div className="mlp-table-wrapper">
        <div className="mlp-table-scroll">
          <table className="mlp-table">
            <thead>
              <tr>
                {headers.map((header, i) => (
                  <th key={i}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={headers.length} className="mlp-center-text">
                    Loading...
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={headers.length}
                    className="mlp-center-text mlp-no-records"
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                currentItems.map((lp, idx) => (
                  <tr key={idx}>
                    <td>{lp.name}</td>
                    <td>{lp.created_by}</td>
                    <td>{lp.total_credits ?? 0}</td>
                    <td>{lp.total_courses ?? 0}</td>
                    <td>{lp.earliest_start_date?.split("T")[0] || "-"}</td>
                    <td>{lp.latest_end_date?.split("T")[0] || "-"}</td>
                    <td>₹{lp.cost_estimation?.toLocaleString('en-IN') || 0}</td>
                    <td>
                      <button
                        className="mlp-action-btn mlp-edit-btn"
                        onClick={() => handleEdit(lp.id)}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="mlp-action-btn mlp-delete-btn"
                        onClick={() => handleDeleteClick(lp.id)}
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        items={filteredPaths}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />

      {showForm && (
        <div className="mlp-modal-overlay">
          <div className="mlp-modal ">
            <div className="mlp-modal-header">
              <h3>Create New Learning Path</h3>
              <button
                className="mlp-modal-close"
                onClick={() => setShowForm(false)}
              >
                ×
              </button>
            </div>
            <div className="mlp-modal-body">
              <form onSubmit={handleCreateSubmit} className="mlp-form">
                <label>
                  Name<span className="required">*</span>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </label>
                <label>
                  Description
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </label>
                <div className="mlp-form-actions">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="mlp-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="mlp-submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? "Creating..." : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="mlp-modal-overlay">
          <div className="mlp-modal">
            <h3>Confirmation</h3>
            <p>Are you sure you want to delete this learning path?</p>
            <div className="mlp-form-actions">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="mlp-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="mlp-submit-btn mlp-delete-confirm-btn"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteLoading && (
        <div className="mlp-loading-overlay">
          <div className="mlp-loading-content">
            <p>Deleting, please don't press back or refresh<span className="mlp-loading-dots"></span></p>
          </div>
        </div>
      )}

      {snackbar.open && (
        <div className={`mlp-snackbar mlp-snackbar-${snackbar.type}`}>
          {snackbar.message}
          <button
            className="mlp-snackbar-close"
            onClick={handleSnackbarClose}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default ManageLearningPaths;